import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { db, storage } from '../../config/firebase.js';
import { updateDoc, deleteDoc, addDoc, collection, doc, Timestamp } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { CSVLink } from 'react-csv';

import UnverifiedTable from './UnverifiedTable';
import ReportedTable from './ReportedTable';
import RenameDialog from './RenameDialog';
import Spinner from '../../utils/Spinner';
import Toast from '../../utils/Toast';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import logger from '../../utils/logger';
import { useUser } from '../../utils/UserContext';
import useModerators, { moderatorName } from '../../utils/useModerators';
import useModerationQueue from './useModerationQueue';
import useNotifications from '../../utils/useNotifications';
import {
  GRADE_SUBJECT_PAIRS,
  SUBJECTS,
  GRADE_NAMES,
  cleanGrade,
  gradeLabel,
  pairsForScopes,
  scopeLabel,
  scopesCover,
  subjectLabel,
} from '../../utils/catalogue';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return new Date(value);
  return value.toDate ? value.toDate() : null;
};

const ModerationPage = () => {
  const { currentUser, profile, role } = useUser();
  const isAdmin = role === 'admin';
  const scopes = useMemo(() => profile?.moderationScopes || [], [profile]);

  // REQ-01: administrators see every queue; moderators see only their scopes
  const pairs = useMemo(
    () => (isAdmin ? GRADE_SUBJECT_PAIRS : pairsForScopes(scopes)),
    [isAdmin, scopes]
  );
  const { items, loading, errors } = useModerationQueue(pairs);
  const { coverage } = useModerators(isAdmin);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'modifiedAt', direction: 'ascending' });
  const [activeTab, setActiveTab] = useState('unverified');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [toast, setToast] = useState({ visible: false, message: '', variant: 'warning' });
  const [renaming, setRenaming] = useState(null);

  const showToast = (message, variant = 'warning') => setToast({ visible: true, message, variant });

  // REQ-02 dashboard indicators: remember the previous visit so new uploads
  // can be highlighted, then record this visit and clear notifications.
  const { unreadCount, markAllRead } = useNotifications(currentUser?.uid);
  const lastVisitRef = useRef(undefined);
  if (lastVisitRef.current === undefined && profile) {
    lastVisitRef.current = profile.lastModerationVisit?.toMillis?.() || null;
  }
  const visitRecorded = useRef(false);
  useEffect(() => {
    if (!currentUser || !profile || visitRecorded.current) return;
    visitRecorded.current = true;
    updateDoc(doc(db, 'users', currentUser.uid), { lastModerationVisit: Timestamp.now() })
      .catch((err) => logger.error('Error recording moderation visit:', err));
  }, [currentUser, profile]);
  useEffect(() => {
    if (unreadCount > 0) markAllRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  const isNew = useCallback(
    (pdf) => Boolean(lastVisitRef.current) && (toDate(pdf.modifiedAt)?.getTime() || 0) > lastVisitRef.current,
    []
  );

  const assignedTo = useCallback(
    (pdf) => coverage[`${pdf.grade}/${pdf.subject}`] || [],
    [coverage]
  );

  const unverifiedPDFs = useMemo(() => items.filter((p) => p.verified !== true), [items]);
  const reportedPDFs = useMemo(() => items.filter((p) => p.reported === true), [items]);
  const newCount = useMemo(() => unverifiedPDFs.filter(isNew).length, [unverifiedPDFs, isNew]);
  const oldestWaitingDays = useMemo(() => {
    const times = unverifiedPDFs.map((p) => toDate(p.modifiedAt)?.getTime()).filter(Boolean);
    if (times.length === 0) return null;
    return Math.floor((Date.now() - Math.min(...times)) / 86400000);
  }, [unverifiedPDFs]);
  const pendingInScope = (scope) =>
    unverifiedPDFs.filter((p) => scopesCover([scope], p.subject, p.grade)).length;
  const unassignedCount = useMemo(
    () => (isAdmin ? unverifiedPDFs.filter((p) => assignedTo(p).length === 0).length : 0),
    [isAdmin, unverifiedPDFs, assignedTo]
  );

  const requestSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'descending' ? 'ascending' : 'descending';
    setSortConfig({ key, direction });
  };

  const filteredAndSortedData = (data) => {
    const s = (searchTerm || '').trim().toLowerCase();
    const filtered = data.filter((pdf) => {
      const matchesSearch = s
        ? pdf.title?.toLowerCase().includes(s) ||
          subjectLabel(pdf.subject).toLowerCase().includes(s) ||
          gradeLabel(pdf.grade).toLowerCase().includes(s) ||
          pdf.theme?.toLowerCase().includes(s) ||
          pdf.userID?.toLowerCase().includes(s) ||
          pdf.tags?.some((tag) => tag?.toLowerCase().includes(s))
        : true;
      const matchesSubject = subjectFilter ? pdf.subject === subjectFilter : true;
      const matchesGrade = gradeFilter ? pdf.grade === gradeFilter : true;
      const matchesAssignment = !assignmentFilter
        ? true
        : assignmentFilter === 'unassigned'
          ? assignedTo(pdf).length === 0
          : assignedTo(pdf).length > 0;
      return matchesSearch && matchesSubject && matchesGrade && matchesAssignment;
    });

    const { key, direction } = sortConfig;
    if (!key) return filtered;
    const dir = direction === 'ascending' ? 1 : -1;
    const value = (pdf) => (key === 'modifiedAt' ? toDate(pdf.modifiedAt)?.getTime() || 0 : pdf[key] ?? '');
    return [...filtered].sort((a, b) => (value(a) > value(b) ? dir : value(a) < value(b) ? -dir : 0));
  };

  const csvHeaders = [
    { label: 'Title', key: 'title' },
    { label: 'Tags', key: 'tags' },
    { label: 'Grade', key: 'grade' },
    { label: 'Subject', key: 'subject' },
    { label: 'Uploaded By', key: 'userID' },
    { label: 'Upload Date', key: 'modifiedAt' },
    { label: 'Verified', key: 'verified' },
    { label: 'Reported', key: 'reportAmount' },
    ...(isAdmin ? [{ label: 'Assigned Moderators', key: 'assigned' }] : []),
  ];

  const csvData = filteredAndSortedData(items).map((pdf) => ({
    title: pdf.title || 'No Title',
    tags: pdf.tags?.join(', ') || 'No Tags',
    grade: gradeLabel(pdf.grade),
    subject: subjectLabel(pdf.subject) || 'No Subject',
    userID: pdf.userID || 'Unknown',
    modifiedAt: toDate(pdf.modifiedAt)?.toLocaleString() || 'N/A',
    verified: pdf.verified ? 'Yes' : 'No',
    reportAmount: pdf.reportAmount || 0,
    assigned: assignedTo(pdf).map(moderatorName).join('; ') || 'Unassigned',
  }));

  const actor = currentUser?.email || currentUser?.uid || 'unknown';

  const logEvent = async (event, pdf) => {
    try {
      await addDoc(collection(db, 'analytics_data'), {
        userID: actor,
        event,
        subject: pdf.subject,
        grade: pdf.grade,
        title: pdf.title,
        timestamp: Timestamp.now(),
      });
    } catch (err) {
      logger.error('Error logging event:', err);
    }
  };

  const reportFailure = (err, action) => {
    logger.error(`${action} failed:`, err);
    showToast(
      err?.code === 'permission-denied'
        ? `Could not ${action}: this resource is outside your moderation scope.`
        : `Could not ${action}. Please try again.`,
      'error'
    );
  };

  // Removes the stored file for document uploads; link resources have no file.
  const deleteStoredFile = async (pdf) => {
    if (!pdf.file_path) return;
    try {
      await deleteObject(ref(storage, pdf.file_path));
    } catch (err) {
      logger.error('Error deleting stored file:', err);
    }
  };

  const approvePDF = async (pdf) => {
    if (!pdf.moderationRating || pdf.moderationRating < 1) {
      showToast('Cannot approve: Resource must have moderation rating before approval.');
      return;
    }
    try {
      await updateDoc(pdf.ref, {
        verified: true,
        rating: pdf.rating || 0,
        ratingAmount: pdf.ratingAmount || 0,
        reportAmount: pdf.reportAmount || 0,
        verifiedBy: actor,
        verifiedAt: Timestamp.now(),
      });
      await logEvent('file_Approved', pdf);
      showToast('Resource approved.', 'success');
    } catch (err) {
      reportFailure(err, 'approve this resource');
    }
  };

  const deletePDF = async (pdf) => {
    try {
      await deleteDoc(pdf.ref);
      await logEvent('file_Deleted', pdf);
      await deleteStoredFile(pdf);
      showToast('Resource deleted.', 'success');
    } catch (err) {
      reportFailure(err, 'delete this resource');
    }
  };

  const approveReportedPDF = async (pdf) => {
    try {
      await updateDoc(pdf.ref, {
        reported: false,
        reportAmount: 0,
        reportResolvedBy: actor,
        reportResolvedAt: Timestamp.now(),
      });
      await logEvent('file_Approved', pdf);
      showToast('Report resolved.', 'success');
    } catch (err) {
      reportFailure(err, 'resolve this report');
    }
  };

  const reportSendToModerate = async (pdf) => {
    try {
      await updateDoc(pdf.ref, { verified: false, reported: false });
      await logEvent('file_SentToModeration', pdf);
      showToast('Resource returned to the moderation queue.', 'success');
    } catch (err) {
      reportFailure(err, 'return this resource to moderation');
    }
  };

  const getPublicUrl = (url) => {
    try {
      if (url.includes('/v0/b/')) return url;
      const filePath = url.split('share2teach-be.appspot.com/')[1].split('?')[0];
      return `https://firebasestorage.googleapis.com/v0/b/share2teach-be.appspot.com/o/${filePath.replace(/\//g, '%2F')}?alt=media`;
    } catch {
      return url;
    }
  };

  // Filter dropdowns only offer what this user can actually see
  const visibleSubjects = useMemo(() => {
    const ids = new Set(pairs.map((p) => p.subject));
    return SUBJECTS.filter((s) => ids.has(s.subject));
  }, [pairs]);
  const visibleGrades = useMemo(() => {
    const ids = new Set(pairs.map((p) => p.grade));
    return GRADE_NAMES.filter((g) => ids.has(cleanGrade(g)));
  }, [pairs]);

  const errorCount = Object.values(errors).reduce((a, b) => a + b, 0);
  const hasNoScopes = !isAdmin && pairs.length === 0;

  const tableProps = {
    filteredAndSortedData,
    getPublicUrl,
    requestSort,
    sortConfig,
    showAssignment: isAdmin,
    assignedTo,
    onRename: setRenaming,
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 pb-16">
      <GoogleAnalytics page="ModerationPage" eventType="moderation_page" />
      <Toast
        message={toast.message}
        variant={toast.variant}
        visible={toast.visible}
        onDismiss={() => setToast((t) => ({ ...t, visible: false }))}
      />
      {renaming && (
        <RenameDialog
          pdf={renaming}
          actor={actor}
          actorUid={currentUser?.uid}
          onClose={() => setRenaming(null)}
          onDone={(message) => showToast(message, 'success')}
        />
      )}

      {/* Page header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-2">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Moderation</h1>
        <p className="mt-1 text-sm text-gray-500">Review new submissions and resolve reports to keep the library high quality.</p>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-4">
        {/* Scope summary */}
        <div className="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm">
          {isAdmin ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-gray-700">
                <span className="font-semibold">Administrator view:</span> all moderation queues.
              </span>
              {unassignedCount > 0 && (
                <button
                  type="button"
                  onClick={() => { setActiveTab('unverified'); setAssignmentFilter('unassigned'); }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 hover:bg-amber-200"
                >
                  {unassignedCount} waiting in scopes with no moderator
                </button>
              )}
              <Link to="/moderator-assignments" className="ml-auto text-xs font-medium text-brand-600 hover:text-brand-700">
                Manage moderator assignments →
              </Link>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-700">Your scopes:</span>
              {scopes.length === 0 && <span className="text-gray-500">none assigned</span>}
              {scopes.map((s) => {
                const pending = loading ? 0 : pendingInScope(s);
                return (
                  <span key={s} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700">
                    {scopeLabel(s)}
                    {pending > 0 && <span className="rounded-full bg-brand-600 px-1.5 text-[10px] font-bold text-white">{pending}</span>}
                  </span>
                );
              })}
            </div>
          )}
          {!loading && unverifiedPDFs.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span><span className="font-semibold text-gray-700">{unverifiedPDFs.length}</span> waiting for review</span>
              {newCount > 0 && (
                <span className="text-emerald-700"><span className="font-semibold">{newCount}</span> new since your last visit</span>
              )}
              {oldestWaitingDays !== null && (
                <span className={oldestWaitingDays >= 7 ? 'text-red-600' : ''}>
                  Oldest has waited <span className="font-semibold">{oldestWaitingDays === 0 ? 'less than a day' : `${oldestWaitingDays} day${oldestWaitingDays === 1 ? '' : 's'}`}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {errorCount > 0 && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div>
              <p className="font-medium">
                {errorCount} of {pairs.length} queues could not be loaded, so this list may be incomplete.
              </p>
              <p className="text-xs mt-0.5">
                {errors['permission-denied']
                  ? 'Access was denied. Ask an administrator to confirm your role and scopes, and that the latest Firestore rules are deployed.'
                  : `Error: ${Object.keys(errors).join(', ')}. Refresh the page to try again.`}
              </p>
            </div>
          </div>
        )}

        {hasNoScopes ? (
          <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-xl border border-gray-200">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-600 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
            </div>
            <p className="text-sm font-medium text-gray-700">No moderation scopes assigned yet</p>
            <p className="text-xs text-gray-500 mt-1 max-w-sm">
              An administrator needs to assign you to one or more subjects and grades before resources appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Toolbar: tabs left, export right */}
            <div className="flex items-center justify-between gap-4 mb-5">
              <div className="flex border-b border-gray-200">
                <button
                  className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === 'unverified'
                      ? 'border-brand-600 text-brand-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('unverified')}
                >
                  Unverified
                  {unverifiedPDFs.length > 0 && (
                    <span className={`ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      activeTab === 'unverified' ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-500'
                    }`}>{unverifiedPDFs.length}</span>
                  )}
                </button>
                <button
                  className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === 'reported'
                      ? 'border-brand-600 text-brand-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('reported')}
                >
                  Reported
                  {reportedPDFs.length > 0 && (
                    <span className={`ml-1.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      activeTab === 'reported' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                    }`}>{reportedPDFs.length}</span>
                  )}
                </button>
              </div>

              <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename="pdf_moderation_data.csv"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Export CSV
              </CSVLink>
            </div>

            {/* Search & filter */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center mb-5">
              <div className="relative flex-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  type="text"
                  placeholder="Search title, theme, subject, grade, tags or uploader…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-shadow"
                />
              </div>
              <select
                value={subjectFilter}
                onChange={(e) => setSubjectFilter(e.target.value)}
                className="w-full sm:w-48 py-2 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-shadow"
              >
                <option value="">All Subjects</option>
                {visibleSubjects.map((s) => (
                  <option key={s.subject} value={s.subject}>{s.label}</option>
                ))}
              </select>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="w-full sm:w-36 py-2 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-shadow"
              >
                <option value="">All Grades</option>
                {visibleGrades.map((g) => (
                  <option key={g} value={cleanGrade(g)}>{g}</option>
                ))}
              </select>
              {isAdmin && (
                <select
                  value={assignmentFilter}
                  onChange={(e) => setAssignmentFilter(e.target.value)}
                  className="w-full sm:w-40 py-2 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-shadow"
                >
                  <option value="">Any assignment</option>
                  <option value="unassigned">Unassigned scopes</option>
                  <option value="assigned">Assigned scopes</option>
                </select>
              )}
            </div>

            {loading && <Spinner size="lg" text="Loading resources…" />}

            {!loading && activeTab === 'unverified' && (
              <UnverifiedTable
                {...tableProps}
                unverifiedPDFs={unverifiedPDFs}
                isNew={isNew}
                approvePDF={approvePDF}
                deletePDF={deletePDF}
              />
            )}

            {!loading && activeTab === 'reported' && (
              <ReportedTable
                {...tableProps}
                reportedPDFs={reportedPDFs}
                approveReportedPDF={approveReportedPDF}
                reportSendToModerate={reportSendToModerate}
                deleteReportedPDF={deletePDF}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ModerationPage;
